import React, { useState } from 'react';
import { SettingsIcon, SaveIcon, PlusIcon, TrashIcon, Edit2Icon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
const SchemeTemplateConfig: React.FC = () => {
  const [activeScheme, setActiveScheme] = useState('Visa');
  const templates = [{
    id: 1,
    scheme: 'Visa',
    version: 'VROL 2.1',
    format: 'CSV',
    lastUpdated: '2023-09-15',
    status: 'Active'
  }, {
    id: 2,
    scheme: 'Mastercard',
    version: 'Mastercom API/XML',
    format: 'XML',
    lastUpdated: '2023-08-22',
    status: 'Active'
  }, {
    id: 3,
    scheme: 'UnionPay',
    version: 'UPI Disp V1.4',
    format: 'CSV',
    lastUpdated: '2023-05-10',
    status: 'Active'
  }];
  const fieldMappings = [{
    internalField: 'schemeCaseId',
    schemeField: 'Case_ID',
    required: true,
    type: 'String',
    length: '15'
  }, {
    internalField: 'arn',
    schemeField: 'ARN',
    required: true,
    type: 'String',
    length: '23'
  }, {
    internalField: 'txId',
    schemeField: 'Merchant_Ref',
    required: true,
    type: 'String',
    length: '32'
  }, {
    internalField: 'amount',
    schemeField: 'Dispute_Amt',
    required: true,
    type: 'Decimal',
    length: '10,2'
  }, {
    internalField: 'reasonCode',
    schemeField: 'Reason_Code',
    required: true,
    type: 'String',
    length: '4'
  }, {
    internalField: 'responseType',
    schemeField: 'Action_Code',
    required: true,
    type: 'String',
    length: '2'
  }, {
    internalField: 'evidenceRef',
    schemeField: 'Doc_Ref_ID',
    required: false,
    type: 'String',
    length: '50'
  }];
  const columns = [{
    header: 'Internal Field',
    accessor: 'internalField',
    className: 'font-medium text-gray-900 dark:text-white'
  }, {
    header: 'Scheme File Header / Tag',
    accessor: 'schemeField',
    className: 'font-mono text-sm text-blue-600 dark:text-blue-400'
  }, {
    header: 'Required',
    accessor: (row: any) => row.required ? <span className="text-red-600 font-medium" data-id="element-4236">Yes</span> : <span className="text-gray-500" data-id="element-4237">No</span>
  }, {
    header: 'Data Type',
    accessor: 'type'
  }, {
    header: 'Max Length',
    accessor: 'length'
  }, {
    header: 'Actions',
    accessor: () => <div className="flex space-x-2 justify-end" data-id="element-4238">
          <button className="text-gray-400 hover:text-blue-600" data-id="element-4239">
            <Edit2Icon className="h-4 w-4" data-id="element-4240" />
          </button>
          <button className="text-gray-400 hover:text-red-600" data-id="element-4241">
            <TrashIcon className="h-4 w-4" data-id="element-4242" />
          </button>
        </div>
  }];
  return <div className="space-y-6" data-id="element-4243">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-4244">
        <div data-id="element-4245">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-4246">
            Scheme Template Configuration
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-4247">
            Configure file structures, field mappings, and validation rules for
            different card schemes.
          </p>
        </div>
        <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-4248">
          <SaveIcon className="h-4 w-4 mr-2" data-id="element-4249" />
          Save Configuration
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-4250">
        <div className="lg:col-span-1 space-y-4" data-id="element-4251">
          <Card title="Active Templates" data-id="element-4252">
            <div className="space-y-2" data-id="element-4253">
              {templates.map(t => <button key={t.id} onClick={() => setActiveScheme(t.scheme)} className={`w-full flex flex-col items-start p-3 rounded-lg border transition-all ${activeScheme === t.scheme ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'}`} data-id="element-4254">
                  <div className="flex justify-between w-full mb-1" data-id="element-4255">
                    <span className={`font-medium ${activeScheme === t.scheme ? 'text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-white'}`} data-id="element-4256">
                      {t.scheme}
                    </span>
                    <span className="text-xs bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 px-2 py-0.5 rounded-full" data-id="element-4257">
                      {t.status}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-4258">
                    {t.version} • {t.format}
                  </span>
                </button>)}
            </div>
            <button className="mt-4 w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-4259">
              <PlusIcon className="h-4 w-4 mr-2" data-id="element-4260" />
              Add New Scheme
            </button>
          </Card>
        </div>

        <div className="lg:col-span-3 space-y-6" data-id="element-4261">
          <Card data-id="element-4262">
            <div className="flex justify-between items-center mb-6" data-id="element-4263">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white flex items-center" data-id="element-4264">
                <SettingsIcon className="h-5 w-5 mr-2 text-gray-400" data-id="element-4265" />
                {activeScheme} File Settings
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-4266">
              <div data-id="element-4267">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4268">
                  Template Version Name
                </label>
                <input type="text" defaultValue={templates.find(t => t.scheme === activeScheme)?.version} className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-800 dark:text-white" data-id="element-4269" />
              </div>
              <div data-id="element-4270">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4271">
                  File Format
                </label>
                <select defaultValue={templates.find(t => t.scheme === activeScheme)?.format} className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-800 dark:text-white" data-id="element-4272">
                  <option value="CSV" data-id="element-4273">CSV (Comma Separated)</option>
                  <option value="XML" data-id="element-4274">XML</option>
                  <option value="Fixed" data-id="element-4275">Fixed Length</option>
                </select>
              </div>
              {activeScheme !== 'Mastercard' && <>
                  <div data-id="element-4276">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4277">
                      Delimiter
                    </label>
                    <input type="text" defaultValue="," className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-800 dark:text-white" data-id="element-4278" />
                  </div>
                  <div data-id="element-4279">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4280">
                      Has Header Row
                    </label>
                    <select defaultValue="Yes" className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-800 dark:text-white" data-id="element-4281">
                      <option value="Yes" data-id="element-4282">Yes</option>
                      <option value="No" data-id="element-4283">No</option>
                    </select>
                  </div>
                </>}
            </div>
          </Card>

          <Card data-id="element-4284">
            <div className="flex justify-between items-center mb-4" data-id="element-4285">
              <h3 className="text-md font-medium text-gray-900 dark:text-white" data-id="element-4286">
                Field Mappings
              </h3>
              <button className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-4287">
                <PlusIcon className="h-4 w-4 mr-1" data-id="element-4288" />
                Add Field
              </button>
            </div>
            <Table columns={columns} data={fieldMappings} keyField="internalField" pagination={false} data-id="element-4289" />
          </Card>
        </div>
      </div>
    </div>;
};
export default SchemeTemplateConfig;